// JSON-LD builders for the entity graph. All output is plain objects that get
// JSON.stringify'd into <script type="application/ld+json"> in a route head.
// Rule: FAQPage schema must only be emitted on pages whose visible text shows
// the exact same questions and answers (Google drops mismatched rich results).

import { SITE_URL, ORG_ID, absolute } from "./site";

export interface QA {
  q: string;
  a: string;
}

export interface Crumb {
  name: string;
  path: string;
}

/** BreadcrumbList — belongs on every page except the home page. */
export function breadcrumbList(crumbs: Crumb[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absolute(c.path),
    })),
  };
}

/** FAQPage — only where the same Q&As are visible on the page. */
export function faqPage(pagePath: string, items: QA[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${absolute(pagePath)}#faq`,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };
}

export interface ServiceSchemaInput {
  path: string;
  name: string;
  serviceType: string;
  description: string;
  /** Optional serviceOutput / audience note. */
  areaServed?: Array<{ type: "Country" | "Place"; name: string }>;
}

/** Service — one per service/standard/registration page. */
export function serviceSchema(input: ServiceSchemaInput) {
  const area = (input.areaServed ?? [{ type: "Country", name: "Iraq" }]).map(
    (a) => ({ "@type": a.type, name: a.name }),
  );
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${absolute(input.path)}#service`,
    name: input.name,
    serviceType: input.serviceType,
    provider: { "@id": ORG_ID },
    areaServed: area,
    description: input.description,
    url: absolute(input.path),
  };
}

export interface ArticleSchemaInput {
  path: string;
  headline: string;
  description: string;
  datePublished: string;
  dateModified?: string;
  inLanguage: "en" | "ar";
  image?: string;
}

/** Article — one per insight/guide page. Authored + published by the org. */
export function articleSchema(input: ArticleSchemaInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${absolute(input.path)}#article`,
    headline: input.headline,
    description: input.description,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    inLanguage: input.inLanguage,
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    mainEntityOfPage: absolute(input.path),
    image: input.image ?? `${SITE_URL}/og-image.jpg`,
  };
}

/**
 * Combine multiple JSON-LD nodes into one script payload via @graph.
 * Pass the result to JSON.stringify in the route head `scripts` entry.
 */
export function graph(...nodes: Array<Record<string, unknown>>) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.map(({ "@context": _ctx, ...rest }) => rest),
  };
}

export { SITE_URL };
